import React, { useEffect, useState, useContext } from 'react';
import {
  Paper, Typography, TableBody, TableCell, TableHead, TableRow,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, Grid, 
  IconButton, Chip, MenuItem, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import ScrollTable, { EMBEDDED_MAX_HEIGHT } from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';

const emptyForm = {
  type: 'individual',
  name: '',
  primaryContactPhone: '',
  primaryContactEmail: '',
  secondaryContactPhone: '',
  secondaryContactEmail: '',
  addressStreet: '',
  addressCountry: 'Uganda',
  notes: '',
  managementFeePercentage: 0,
  paymentDate: 1,
  // '' = not agreed. The server stores it as NULL and refuses security deposits for this
  // landlord's tenants until one of the two is chosen.
  securityHeldBy: '',
  isActive: true
};

const LandlordManagement = () => {
  const { formatMoney } = useCurrency();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const [landlords, setLandlords] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server search there instead.
  const {
    query, setQuery, filterValues, setFilter, reset,
    rows: visibleLandlords, resultCount, totalCount
  } = useTableSearch(landlords, {
    searchFields: ['name', 'primaryContactPhone', 'primaryContactEmail', 'addressStreet'],
    filters: {
      type: (row, v) => row.type === v,
      isActive: (row, v) => String(row.isActive !== false) === v
    }
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selectedLandlord, setSelectedLandlord] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loadingProps, setLoadingProps] = useState(false);

  useEffect(() => { 
    fetchLandlords(); 
  }, []);

  const fetchLandlords = async () => {
    try {
      const res = await axios.get('/landlords');
      setLandlords(res.data);
    } catch (error) {
      toast.error('Error fetching landlords');
    }
  };

  const fetchProperties = async (landlordId) => {
    setLoadingProps(true);
    try {
      const res = await axios.get(`/landlords/${landlordId}/properties`);
      setProperties(res.data);
    } catch (error) {
      toast.error('Error fetching properties');
    }
    setLoadingProps(false);
  };

  const handleOpen = (landlord = null) => {
    if (landlord) {
      setEditing(landlord._id);
      setForm({
        type: landlord.type || 'individual',
        name: landlord.name || '',
        primaryContactPhone: landlord.primaryContactPhone || '',
        primaryContactEmail: landlord.primaryContactEmail || '',
        secondaryContactPhone: landlord.secondaryContactPhone || '',
        secondaryContactEmail: landlord.secondaryContactEmail || '',
        addressStreet: landlord.addressStreet || '',
        addressCountry: landlord.addressCountry || 'Uganda',
        notes: landlord.notes || '',
        managementFeePercentage: landlord.managementFeePercentage || 0,
        paymentDate: landlord.paymentDate || 1,
        securityHeldBy: landlord.securityHeldBy || '',
        isActive: landlord.isActive !== undefined ? landlord.isActive : true
      });
    } else {
      setEditing(null);
      setForm(emptyForm);
    }
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (!form.name) return toast.error('Name is required');

      if (editing) {
        // Staff can only update non-critical fields (not isActive)
        const updateData = { ...form };
        if (!isAdmin) {
          delete updateData.isActive; // Don't allow staff to change active status
        }
        await axios.put(`/landlords/${editing}`, updateData);
        toast.success('Landlord updated successfully');
      } else {
        // Only admin can create landlords
        if (!isAdmin) {
          toast.error('Only administrators can create new landlords');
          return;
        }
        await axios.post('/landlords', form);
        toast.success('Landlord created successfully');
      }
      setOpen(false);
      fetchLandlords();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save landlord');
    }
  };

  const handleViewProperties = (landlord) => {
    if (selectedLandlord && selectedLandlord._id === landlord._id) {
      setSelectedLandlord(null);
      setProperties([]);
    } else {
      setSelectedLandlord(landlord);
      fetchProperties(landlord._id);
    }
  };

  // Dashboard metrics for selected landlord
  const summary = () => {
    const count = properties.length;
    // Rent roll calculation would need to fetch units/allocations
    const rentRoll = 0; // Placeholder - would need to calculate from units/allocations
    const occupied = properties.filter(p => p.status === 'occupied').length;
    const unavailable = properties.filter(p => p.status === 'unavailable').length;
    return { count, rentRoll, occupied, available: count - occupied - unavailable, unavailable };
  };

  const s = summary();

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Landlord Management</Typography>
        {isAdmin && (
          <Button variant="contained" onClick={() => handleOpen()}>
            Add Landlord
          </Button>
        )}
      </Box>

      {visibleLandlords.map((landlord) => (
        <Accordion 
          key={landlord._id} 
          expanded={selectedLandlord?._id === landlord._id}
          onChange={() => handleViewProperties(landlord)}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" width="100%">
              {landlord.type === 'company' ? (
                <BusinessIcon color="primary" sx={{ mr: 2 }} />
              ) : (
                <PersonIcon color="primary" sx={{ mr: 2 }} />
              )}
              <Box flex={1}>
                <Typography variant="h6">{landlord.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {landlord.primaryContactPhone}
                  {/* The landlord's blanket default. Individual properties and units may
                      be on their own terms — including a flat monthly amount — so this is
                      labelled as the default rather than stated as the fee. */}
                  {' • '}Default fee: {landlord.managementFeePercentage ?? 10}%
                  {/* Labelled as a default, because it is one. The pay day that actually
                      governs payment is on each property — a landlord with several
                      buildings can be paid on a different day for each. */}
                  {' • '}Default pay day: {landlord.paymentDate ?? 1}
                  {/* Where a tenant's security deposit goes. Shown even when unset, because
                      "not agreed" is the state that blocks recording one. */}
                  {' • '}Deposits held by: {
                    landlord.securityHeldBy === 'agency' ? 'Agency'
                      : landlord.securityHeldBy === 'landlord' ? 'Landlord'
                        : 'not agreed'
                  }
                </Typography>
              </Box>
              <Chip
                label={landlord.isActive ? 'Active' : 'Inactive'}
                color={landlord.isActive ? 'success' : 'default'}
                size="small"
                sx={{ mr: 2 }}
              />
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpen(landlord);
                }}
                size="small"
              >
                <EditIcon />
              </IconButton>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {loadingProps ? (
              <Typography>Loading properties...</Typography>
            ) : selectedLandlord?._id === landlord._id ? (
              <>
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, bgcolor: '#1976d2', color: '#fff', minHeight: 90 }}>
                      <Typography variant="caption">Total Properties</Typography>
                      <Typography variant="h5">{s.count}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, bgcolor: '#7b1fa2', color: '#fff', minHeight: 90 }}>
                      <Typography variant="caption">Rent Roll</Typography>
                      <Typography variant="h5">{formatMoney(s.rentRoll, { decimals: 0 })}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, bgcolor: '#388e3c', color: '#fff', minHeight: 90 }}>
                      <Typography variant="caption">Occupied</Typography>
                      <Typography variant="h5">{s.occupied}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, bgcolor: '#ffd600', color: '#222', minHeight: 90 }}>
                      <Typography variant="caption">Available</Typography>
                      <Typography variant="h5">{s.available}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, bgcolor: '#d32f2f', color: '#fff', minHeight: 90 }}>
                      <Typography variant="caption">Unavailable</Typography>
                      <Typography variant="h5">{s.unavailable}</Typography>
                    </Paper>
                  </Grid>
                </Grid>
                <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>
                  Properties
                </Typography>
      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Name, phone or email"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
        filters={[
          { key: 'type', label: 'Type', value: filterValues.type || '',
            onChange: (v) => setFilter('type', v),
            options: [{ value: 'individual', label: 'Individual' }, { value: 'company', label: 'Company' }] },
          { key: 'isActive', label: 'Status', value: filterValues.isActive || '',
            onChange: (v) => setFilter('isActive', v), allLabel: 'All statuses',
            options: [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }] }
        ]}
      />
                <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Details</TableCell>
                        <TableCell>Tenants/Allocations</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {properties.map((p) => (
                        <TableRow key={p._id}>
                          <TableCell>{p.propertyName}</TableCell>
                          <TableCell>
                            <Chip
                              label={p.propertyType === 'commercial' ? 'Commercial' : 'Residential'}
                              color={p.propertyType === 'commercial' ? 'secondary' : 'primary'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={p.status}
                              color={
                                p.status === 'occupied'
                                  ? 'success'
                                  : p.status === 'available'
                                  ? 'primary'
                                  : p.status === 'unavailable'
                                  ? 'error'
                                  : 'warning'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {p.propertyType === 'commercial' ? (
                              <Typography variant="body2">
                                {p.totalSquareFeet || p.squareFeet || 0} sq ft
                                {p.allocatedSquareFeet !== undefined && (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {p.allocatedSquareFeet} allocated
                                  </Typography>
                                )}
                              </Typography>
                            ) : (
                              <Typography variant="body2">
                                {p.unitsCount || p.numberOfUnits || 0} units
                                {p.occupiedUnitsCount !== undefined && (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {p.occupiedUnitsCount} occupied
                                  </Typography>
                                )}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {p.propertyType === 'commercial' ? (
                              <Typography variant="body2">
                                {p.allocationsCount || 0} allocation(s)
                              </Typography>
                            ) : (
                              <Typography variant="body2">
                                {p.occupiedUnitsCount || 0} tenant(s)
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {properties.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">No properties found</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </ScrollTable>
              </>
            ) : null}
          </AccordionDetails>
        </Accordion>
      ))}

      {visibleLandlords.length === 0 && (
        <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
          No landlords found
        </Typography>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit' : 'Add'} Landlord</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <TextField
                select
                label="Type"
                fullWidth
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                SelectProps={{ native: true }}
                disabled={!isAdmin && editing}
              >
                <option value="individual">Individual</option>
                <option value="company">Company</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Name"
                fullWidth
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Primary Contact
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Primary Phone"
                fullWidth
                value={form.primaryContactPhone}
                onChange={(e) => setForm({ ...form, primaryContactPhone: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Primary Email"
                fullWidth
                type="email"
                value={form.primaryContactEmail}
                onChange={(e) => setForm({ ...form, primaryContactEmail: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Secondary Contact (Optional)
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Secondary Phone"
                fullWidth
                value={form.secondaryContactPhone}
                onChange={(e) => setForm({ ...form, secondaryContactPhone: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Secondary Email"
                fullWidth
                type="email"
                value={form.secondaryContactEmail}
                onChange={(e) => setForm({ ...form, secondaryContactEmail: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Address
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Street"
                fullWidth
                value={form.addressStreet}
                onChange={(e) => setForm({ ...form, addressStreet: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Country"
                fullWidth
                value={form.addressCountry}
                onChange={(e) => setForm({ ...form, addressCountry: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Management Fee Percentage"
                fullWidth
                required
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                value={form.managementFeePercentage || 0}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setForm({ ...form, managementFeePercentage: parseFloat(e.target.value) || 0 })}
                helperText="% of rent charged as management fee"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Default Pay Day (Day of Month)"
                fullWidth
                required
                type="number"
                inputProps={{ min: 1, max: 31, step: 1 }}
                value={form.paymentDate || 1}
                onChange={(e) => {
                  const val = Math.min(31, Math.max(1, parseInt(e.target.value) || 1));
                  setForm({ ...form, paymentDate: val });
                }}
                helperText="Applied to new properties for this landlord. Each property carries its own pay day, set on the property, and that is what governs payment."
              />
            </Grid>
            <Grid item xs={12}>
              {/* Agreed per landlord, for all their properties. Each deposit copies the
                  choice when it is received, so changing this affects deposits taken from
                  now on and never one already held. */}
              <TextField
                select
                fullWidth
                label="Security deposits held by"
                value={form.securityHeldBy || ''}
                onChange={(e) => setForm({ ...form, securityHeldBy: e.target.value })}
                helperText={
                  form.securityHeldBy === 'agency'
                    ? 'The agency keeps tenants’ deposits and refunds them itself at move-out.'
                    : form.securityHeldBy === 'landlord'
                      ? 'Deposits are paid over to the landlord (commission is earned on them); refunds are deducted from the landlord’s pay.'
                      : 'Not agreed yet — security deposits cannot be recorded for this landlord’s tenants until this is set.'
                }
              >
                <MenuItem value="">Not agreed</MenuItem>
                <MenuItem value="agency">Agency</MenuItem>
                <MenuItem value="landlord">Landlord</MenuItem>
              </TextField>
            </Grid>
            {editing && isAdmin && (
              <Grid item xs={12}>
                <TextField
                  select
                  label="Status"
                  fullWidth
                  value={form.isActive ? 'active' : 'inactive'}
                  onChange={(e) =>
                    setForm({ ...form, isActive: e.target.value === 'active' })
                  }
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default LandlordManagement;

